import React, { createContext, useContext, useMemo, useReducer } from 'react';
import type { FC } from 'react';
import type { AxiosInstance } from 'axios';
import type {
  AppUpdatesContextStateType,
  AppUpdatesActionsType,
  UpdatesStateType,
  BundlesStateType,
} from './types';
import {
  authReducer,
  deriveUserFields,
  updatesReducer,
  bundlesReducer,
  INITIAL_AUTH_STATE,
  INITIAL_UPDATES_STATE,
  INITIAL_BUNDLES_STATE,
} from './reducers';

type PartialUpdatesState = Partial<UpdatesStateType> & {
  draftFilters?: Partial<UpdatesStateType['draftFilters']>;
  committedFilters?: Partial<UpdatesStateType['committedFilters']>;
  pagination?: Partial<UpdatesStateType['pagination']>;
};

type PartialBundlesState = Partial<BundlesStateType> & {
  draftFilters?: Partial<BundlesStateType['draftFilters']>;
  committedFilters?: Partial<BundlesStateType['committedFilters']>;
  pagination?: Partial<BundlesStateType['pagination']>;
};

interface AppUpdatesContextInitialState {
  user?: AppUpdatesContextStateType['user'];
  updates?: PartialUpdatesState;
  bundles?: PartialBundlesState;
}

interface AppUpdatesContextType {
  dispatch: React.Dispatch<AppUpdatesActionsType>;
  state: AppUpdatesContextStateType;
  api: AxiosInstance;
}

const INITIAL_STATE: AppUpdatesContextStateType = {
  ...INITIAL_AUTH_STATE,
  updates: INITIAL_UPDATES_STATE,
  bundles: INITIAL_BUNDLES_STATE,
};

function reducer(
  state: AppUpdatesContextStateType,
  action: AppUpdatesActionsType,
): AppUpdatesContextStateType {
  switch (action.type) {
    case 'AUTHENTICATE': {
      const authState = authReducer(
        {
          user: state.user,
          isLoggedIn: state.isLoggedIn,
          loggedInUser: state.loggedInUser,
        },
        action,
      );
      return {
        ...state,
        ...authState,
      };
    }

    case 'UPDATES_CURSOR_SET':
    case 'UPDATES_LIMIT_SET':
    case 'UPDATES_DATA_LINKS_SET':
    case 'UPDATES_LOADING_SET':
    case 'UPDATES_ERROR_SET':
    case 'FILTER_APP_SELECT':
    case 'FILTER_STATUS_SELECT':
    case 'FILTER_CHANGE_TYPE_SELECT':
    case 'FILTER_IMPACT_RATING_SELECT':
    case 'FILTER_TAG_SELECT':
    case 'FILTER_SEARCH_SELECT':
    case 'UPDATES_FILTERS_APPLY':
    case 'UPDATES_FILTERS_RESET':
    case 'FILTER_EFFECTIVE_FROM_TOGGLE':
      return {
        ...state,
        updates: updatesReducer(state.updates, action),
      };

    case 'BUNDLE_FILTER_APP_SELECT':
    case 'BUNDLE_FILTER_DATE_SELECT':
    case 'BUNDLE_FILTER_SEARCH_SELECT':
    case 'BUNDLES_FILTERS_APPLY':
    case 'BUNDLES_FILTERS_RESET':
    case 'BUNDLES_DATA_LINKS_SET':
    case 'BUNDLES_LOADING_SET':
    case 'BUNDLES_ERROR_SET':
    case 'BUNDLES_CURSOR_SET':
    case 'BUNDLES_LIMIT_SET':
      return {
        ...state,
        bundles: bundlesReducer(state.bundles, action),
      };

    default:
      return state;
  }
}

const AppUpdatesContext = createContext<AppUpdatesContextType | null>(null);

export const useAppUpdatesContext = () => {
  const ctx = useContext(AppUpdatesContext);
  if (!ctx) {
    throw new Error(
      'useAppUpdatesContext must be used within <AppUpdatesContextProvider>',
    );
  }
  return ctx;
};

export interface AppUpdatesContextProviderProps {
  api: AxiosInstance;
  initialState?: AppUpdatesContextInitialState;
}

export const AppUpdatesContextProvider: FC<
  React.PropsWithChildren<AppUpdatesContextProviderProps>
> = ({ children, api, initialState }) => {
  const initialUser = initialState?.user ?? INITIAL_STATE.user;
  const initialStateMerged: AppUpdatesContextStateType = {
    user: initialUser,
    ...deriveUserFields(initialUser),
    updates: {
      ...INITIAL_STATE.updates,
      ...initialState?.updates,
      pagination: {
        ...INITIAL_STATE.updates.pagination,
        ...initialState?.updates?.pagination,
      },
      draftFilters: {
        ...INITIAL_STATE.updates.draftFilters,
        ...initialState?.updates?.draftFilters,
      },
      committedFilters: {
        ...INITIAL_STATE.updates.committedFilters,
        ...initialState?.updates?.committedFilters,
      },
    },
    bundles: {
      ...INITIAL_STATE.bundles,
      ...initialState?.bundles,
      pagination: {
        ...INITIAL_STATE.bundles.pagination,
        ...initialState?.bundles?.pagination,
      },
      draftFilters: {
        ...INITIAL_STATE.bundles.draftFilters,
        ...initialState?.bundles?.draftFilters,
      },
      committedFilters: {
        ...INITIAL_STATE.bundles.committedFilters,
        ...initialState?.bundles?.committedFilters,
      },
    },
  };

  const [state, dispatch] = useReducer(reducer, initialStateMerged);

  const value = useMemo(
    () => ({ state, dispatch, api }),
    [state, dispatch, api],
  );

  return (
    <AppUpdatesContext.Provider value={value}>
      {children}
    </AppUpdatesContext.Provider>
  );
};
